import { useState } from "react";

import type { ConceptAsset } from "@digital-twins/shared-types";

import { CreativeMedia } from "@/components/chat/CreativeMedia";

type AssetMedia = ConceptAsset["media"][number];

interface ChoiceOptionThumbnailProps {
  /** The option's own typed asset refs; only a sole ref names its creative. */
  assetRefs: ReadonlyArray<{ assetId: string }> | null | undefined;
  conceptAssets: ReadonlyArray<ConceptAsset>;
  /** The authorized browser URL of a saved image, or undefined when it has none. */
  resolveMediaUrl: (media: AssetMedia) => string | undefined;
  /** The option's own text, used as the alt text. */
  label: string;
  testId: string;
}

/**
 * The image an option stands for, read from the option's typed asset ref (the
 * same ref the compiler uses), never guessed from its position or text.
 * Renders nothing when the option has no single ref, the asset has no image,
 * or the image fails to load.
 */
export function ChoiceOptionThumbnail(props: ChoiceOptionThumbnailProps) {
  const { assetRefs, conceptAssets, resolveMediaUrl, label, testId } = props;

  const assetId = assetRefs?.length === 1 ? assetRefs[0].assetId : undefined;
  const asset = assetId ? conceptAssets.find((concept) => concept.id === assetId) : undefined;

  return (
    <AssetThumbnail asset={asset} resolveMediaUrl={resolveMediaUrl} label={label} testId={testId} />
  );
}

interface AssetThumbnailProps {
  asset: ConceptAsset | undefined;
  resolveMediaUrl: (media: AssetMedia) => string | undefined;
  label: string;
  testId: string;
}

/** One saved image asset as a small thumbnail; nothing when it has no image or fails to load. */
function AssetThumbnail(props: AssetThumbnailProps) {
  const { asset, resolveMediaUrl, label, testId } = props;

  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const media = asset?.media.find((item) => item.kind === "image");
  const src = media ? resolveMediaUrl(media) : undefined;

  if (!src || failedSrc === src) {
    return null;
  }

  return (
    <span
      className="h-8 w-11 shrink-0 overflow-hidden rounded-[4px] border border-border"
      onErrorCapture={() => setFailedSrc(src)}
      data-testid={testId}
    >
      <CreativeMedia
        src={src}
        mimeType={media?.mimeType ?? undefined}
        alt={label}
        className="h-full w-full object-cover object-top"
      />
    </span>
  );
}

interface QuestionExposureThumbnailsProps {
  /** Ids of the assets the question shows respondents (`assetIdsShownInSharedView`). */
  assetIds: ReadonlyArray<string>;
  conceptAssets: ReadonlyArray<ConceptAsset>;
  resolveMediaUrl: (media: AssetMedia) => string | undefined;
  questionId: string;
}

/**
 * A compact row of the creatives a question shows respondents, each labelled by
 * the asset's own name. Renders nothing when none has an image.
 */
export function QuestionExposureThumbnails(props: QuestionExposureThumbnailsProps) {
  const { assetIds, conceptAssets, resolveMediaUrl, questionId } = props;

  const assets = assetIds.flatMap((assetId) => {
    const asset = conceptAssets.find((concept) => concept.id === assetId);
    return asset ? [asset] : [];
  });

  if (assets.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2" data-testid={`question-exposure-thumbnails-${questionId}`}>
      {assets.map((asset, index) => (
        <AssetThumbnail
          key={asset.id}
          asset={asset}
          resolveMediaUrl={resolveMediaUrl}
          label={asset.name}
          testId={`question-exposure-thumbnail-${questionId}-${index}`}
        />
      ))}
    </div>
  );
}
